import type { InteractionMode, InteractionAction } from "./types";

export const INITIAL_INTERACTION_MODE: InteractionMode = { kind: "idle" };

export const interactionModeReducer = (
  state: InteractionMode,
  action: InteractionAction,
): InteractionMode => {
  switch (action.type) {
    case "START_CREATING_ELEMENT": {
      return {
        kind: "creating-element",
        context: { pageIndex: action.pageIndex, selectedGroupName: "" },
        step: "group-selection",
        modifier: "none",
      };
    }
    case "STOP_CREATING_ELEMENT": {
      if (state.kind !== "creating-element") return state;
      return { kind: "idle" };
    }
    case "SET_ELEMENT_CREATION_STEP": {
      if (state.kind !== "creating-element") return state;
      if (action.step === "adding-elements") {
        return { ...state, step: "adding-elements", modifier: "multi-add" };
      }
      return { ...state, step: "group-selection", modifier: "none" };
    }
    case "SET_ELEMENT_CREATION_GROUP_NAME": {
      if (state.kind !== "creating-element") return state;
      return {
        ...state,
        context: { ...state.context, selectedGroupName: action.name },
      };
    }
    case "TOGGLE_EDITING_ELEMENT": {
      if (state.kind === "idle") return { kind: "editing-element" };
      if (state.kind === "editing-element") return { kind: "idle" };
      if (
        state.kind === "creating-element" &&
        state.step === "adding-elements"
      ) {
        return {
          ...state,
          modifier: state.modifier === "editing" ? "none" : "editing",
        };
      }
      return state;
    }
    case "TOGGLE_MULTI_ADD": {
      if (
        state.kind !== "creating-element" ||
        state.step !== "adding-elements"
      ) {
        return state;
      }
      return {
        ...state,
        modifier: state.modifier === "multi-add" ? "none" : "multi-add",
      };
    }
    case "START_DRAWING_ROUTE": {
      return {
        kind: "drawing-route",
        context: { pageIndex: action.pageIndex, selectedGroupName: "" },
        step: "group-selection",
      };
    }
    case "STOP_DRAWING_ROUTE": {
      if (state.kind !== "drawing-route" && state.kind !== "editing-route") {
        return state;
      }
      return { kind: "idle" };
    }
    case "SET_ROUTE_CREATION_STEP": {
      if (state.kind === "drawing-route") {
        return { ...state, step: action.step };
      }
      if (state.kind === "editing-route") {
        return {
          kind: "drawing-route",
          context: state.context,
          step: action.step,
        };
      }
      return state;
    }
    case "SET_ROUTE_CREATION_GROUP_NAME": {
      if (state.kind !== "drawing-route" && state.kind !== "editing-route") {
        return state;
      }
      return {
        ...state,
        context: { ...state.context, selectedGroupName: action.name },
      };
    }
    case "START_EDITING_ROUTE": {
      if (state.kind !== "drawing-route") return state;
      if (state.step !== "drawing") return state;
      return {
        kind: "editing-route",
        context: state.context,
      };
    }
    case "STOP_EDITING_ROUTE": {
      if (state.kind !== "editing-route") return state;
      return {
        kind: "drawing-route",
        context: state.context,
        step: "drawing",
      };
    }
    case "START_PATH_EDITING": {
      if (state.kind !== "idle") return state;
      return { kind: "editing-path" };
    }
    case "STOP_PATH_EDITING": {
      if (state.kind !== "editing-path") return state;
      return { kind: "idle" };
    }
    case "START_SELECTION_AREAS": {
      if (state.kind !== "idle") return state;
      return {
        kind: "selection-areas",
        pageIndex: action.pageIndex,
        step: "list",
      };
    }
    case "SET_SELECTION_AREAS_STEP": {
      if (state.kind !== "selection-areas") return state;
      return { ...state, step: action.step };
    }
    case "STOP_SELECTION_AREAS": {
      if (state.kind !== "selection-areas") return state;
      return { kind: "idle" };
    }
    case "START_DRAWING_ROOMS": {
      if (state.kind !== "idle") return state;
      return {
        kind: "drawing-rooms",
        pageIndex: action.pageIndex,
        step: "drawing",
      };
    }
    case "SET_ROOMS_STEP": {
      if (state.kind !== "drawing-rooms") return state;
      return { ...state, step: action.step };
    }
    case "START_EDITING_ROOM": {
      if (state.kind !== "idle") return state;
      return {
        kind: "editing-room",
        pageIndex: action.pageIndex,
        roomId: action.roomId,
      };
    }
    case "STOP_ROOMS": {
      if (
        state.kind !== "drawing-rooms" &&
        state.kind !== "editing-room" &&
        state.kind !== "merging-rooms"
      ) {
        return state;
      }
      return { kind: "idle" };
    }
    case "START_MERGING_ROOMS": {
      if (state.kind !== "idle" && state.kind !== "editing-room") return state;
      return { kind: "merging-rooms", pageIndex: action.pageIndex };
    }
    case "STOP_MERGING_ROOMS": {
      if (state.kind !== "merging-rooms") return state;
      return { kind: "idle" };
    }
    case "START_CALIBRATING": {
      if (state.kind === "drawing-route" && state.step === "drawing") {
        return {
          kind: "calibrating",
          context: state.context,
          calibration: { phase: "picking-anchor" },
          origin: "embedded",
        };
      }
      if (state.kind === "editing-route") {
        return {
          kind: "calibrating",
          context: state.context,
          calibration: { phase: "picking-anchor" },
          origin: "embedded",
        };
      }
      return state;
    }
    case "START_RECALIBRATING": {
      // Re-calibration entered from the idle Cable Groups view; returns to idle.
      if (state.kind !== "idle") return state;
      return {
        kind: "calibrating",
        context: { pageIndex: action.pageIndex, selectedGroupName: "" },
        calibration: { phase: "picking-anchor" },
        origin: "standalone",
      };
    }
    case "SET_CALIBRATION_POINT": {
      if (state.kind !== "calibrating") return state;
      const { calibration } = state;
      if (calibration.phase === "picking-anchor") {
        return {
          ...state,
          calibration: { phase: "picking-target", anchor: action.point },
        };
      }
      if (calibration.phase === "picking-target") {
        return {
          ...state,
          calibration: {
            phase: "modal",
            anchor: calibration.anchor,
            target: action.point,
          },
        };
      }
      return state;
    }
    case "STOP_CALIBRATING": {
      if (state.kind !== "calibrating") return state;
      if (state.origin === "standalone") return { kind: "idle" };
      return {
        kind: "drawing-route",
        context: state.context,
        step: "drawing",
      };
    }
    case "EXIT_INTERACTION": {
      if (state.kind === "editing-element") return { kind: "idle" };
      if (state.kind === "editing-path") return { kind: "idle" };
      if (state.kind === "editing-route") {
        return {
          kind: "drawing-route",
          context: state.context,
          step: "drawing",
        };
      }
      if (
        state.kind === "creating-element" &&
        state.step === "adding-elements" &&
        state.modifier !== "none"
      ) {
        return { ...state, modifier: "none" };
      }
      return state;
    }
    case "RESET_TO_IDLE": {
      return { kind: "idle" };
    }
    default: {
      return state;
    }
  }
};
